import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import WhatsappFAB from "@/components/WhatsappFAB";
import MobileStickyCTA from "@/components/MobileStickyCTA";
import SEO from "@/components/SEO";
import JsonLd, { caseSchema } from "@/components/JsonLd";
import CaseStudyMedia, { CaseStudyCover } from "@/components/CaseStudyMedia";
import { ArrowLeft, ArrowRight, Quotes, WhatsappLogo } from "@phosphor-icons/react";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/case-studies/slug/${slug}`).then((r) => setItem(r.data)).catch((e) => setErr(e?.response?.status || "err"));
  }, [slug]);

  if (err === 404) return (
    <div className="min-h-screen bg-white">
      <Nav />
      <div className="container-x pt-40 pb-32 text-center">
        <h1 className="font-display font-black text-4xl">Case study not found</h1>
        <Link to="/#cases" className="mt-6 inline-block btn-brand">Back to case studies</Link>
      </div>
      <Footer />
    </div>
  );
  if (!item) return <div className="min-h-screen bg-white" />;

  return (
    <div className="min-h-screen bg-white">
      <SEO title={item.title} description={item.summary} image={item.image_url} />
      <JsonLd data={caseSchema(item, typeof window !== "undefined" ? window.location.origin : "")} />
      <Nav />
      <main className="pt-28 md:pt-36 pb-16">
        <div className="container-x">
          <Link to="/#cases" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-neutral-500 hover:text-[color:var(--brand)]">
            <ArrowLeft size={14} weight="bold" /> All case studies
          </Link>

          <div className="mt-8 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{item.industry}</span>
                <span className="w-4 h-px bg-neutral-300" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--brand)]">{item.client}</span>
              </div>
              <h1 className="mt-4 font-display font-black text-4xl md:text-6xl leading-[1.02] tracking-tight">{item.title}</h1>
              <p className="mt-4 md:mt-6 text-lg md:text-xl text-neutral-700 leading-snug max-w-2xl">{item.summary}</p>

              <CaseStudyCover item={item} />

              {item.body_md && (
                <div className="prose-md mt-10 text-neutral-800">
                  <ReactMarkdown>{item.body_md}</ReactMarkdown>
                </div>
              )}

              <CaseStudyMedia item={item} />

              {item.client_quote && (
                <blockquote data-testid="case-quote" className="mt-12 border-l-4 border-[color:var(--brand)] pl-6 bg-[color:var(--paper-alt)] p-6">
                  <Quotes size={22} weight="fill" className="text-[color:var(--brand)]" />
                  <p className="mt-3 font-display text-xl md:text-2xl leading-snug text-neutral-800">&ldquo;{item.client_quote}&rdquo;</p>
                  <div className="mt-4 font-mono text-[11px] uppercase tracking-widest text-neutral-500">— {item.client_role || item.client}</div>
                </blockquote>
              )}
            </div>

            <aside className="lg:col-span-4">
              <div className="border border-[color:var(--ink)] p-6">
                <div className="eyebrow">Before / after</div>
                <div className="mt-4 divide-y divide-[color:var(--line)]">
                  {(item.metrics || []).map((m, i) => (
                    <div key={i} data-testid={`case-metric-${i}`} className="py-4 first:pt-0 last:pb-0">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{m.label}</div>
                      <div className="mt-2 flex items-center gap-3">
                        <span className="font-display font-bold text-lg text-neutral-500 line-through">{m.before}</span>
                        <ArrowRight size={14} className="text-neutral-400" />
                        <span className="font-display font-black text-2xl text-[color:var(--brand)]">{m.after}</span>
                      </div>
                    </div>
                  ))}
                  {(!item.metrics || item.metrics.length === 0) && (
                    <div className="py-4 text-sm text-neutral-600">{item.result}</div>
                  )}
                </div>
              </div>
              <div className="mt-6 border-2 border-[color:var(--brand)] bg-[color:var(--brand)]/5 p-6">
                <div className="eyebrow">Similar project?</div>
                <h3 className="font-display font-bold text-xl mt-2">{item.cta_label || "Talk to us about a similar build"}</h3>
                <a href={`https://wa.me/7054986771?text=${encodeURIComponent(`I saw the ${item.client} case study — I'd like to discuss a similar project.`)}`}
                  target="_blank" rel="noreferrer" data-testid="case-cta" className="mt-4 btn-brand w-full justify-center text-sm">
                  <WhatsappLogo size={14} weight="fill" /> Chat on WhatsApp
                </a>
                <Link to="/#contact" className="mt-2 btn-ghost w-full justify-center text-sm">
                  Send a brief
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
      <WhatsappFAB />
      <MobileStickyCTA />
    </div>
  );
}
